'use client'

import { SmartHeader } from '@/widgets/smart-header'
import { Footer } from '@/widgets/footer'
import { BuyForm } from '@/widgets/buy-form/ui/BuyForm'
import { motion } from 'framer-motion'
import { TrendingUp, Award, FileCheck, DollarSign, Zap, Clock } from 'lucide-react'

// Metadata moved to layout or separate metadata file

export default function BuyOOOMoscowPage() {
  return (
    <div className="relative flex min-h-screen flex-col">
      <SmartHeader />
      <main className="flex-1 pt-14">
        {/* Hero секция */}
        <section className="container pt-12 md:pt-20 pb-6 md:pb-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-6 text-3xl md:text-5xl font-bold">Купить готовое ООО в Москве</h1>
            <p className="mb-8 text-xl text-muted-foreground">
              Готовые фирмы в Москве с историей, банковскими счетами и лицензиями. Подбор за 2-3
              дня, оформление за 1-2 дня.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>Московская регистрация</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>Быстрый старт в столице</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-500">✓</span>
                <span>Престижный адрес</span>
              </div>
            </div>
          </div>
        </section>

        {/* Форма заявки */}
        <section className="container py-3 bg-muted/20">
          <div className="mx-auto max-w-2xl">
            <BuyForm />
          </div>
        </section>

        {/* Преимущества ООО в Москве */}
        <section className="container py-6 md:py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl md:text-3xl font-bold text-center mb-4 md:mb-6"
            >
              Преимущества покупки ООО в Москве
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Престиж столицы</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Московский адрес регистрации повышает доверие клиентов и партнеров по всей
                    России.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Лучшие возможности</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Доступ к крупнейшему рынку России, федеральным тендерам и московским
                    госзакупкам.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Банковское обслуживание</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Лучшие условия в банках, быстрое открытие счетов, доступ к кредитам и
                    эквайрингу.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Московские лицензии</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    ООО с лицензиями, выданными московскими ведомствами, действуют по всей РФ.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Быстрое оформление</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Личные встречи в Москве, быстрое оформление документов, нотариальное заверение.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex items-start gap-3 md:gap-4 p-4 md:p-6 rounded-lg border hover:border-orange-500 transition-colors bg-background"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 md:w-8 md:h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-semibold mb-2">Деловая среда</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Участие в московских бизнес-процессах, networking, доступ к инвесторам.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Типы ООО в Москве */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Виды готовых ООО в Москве
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏢</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Чистые ООО</h3>
                <p className="text-muted-foreground mb-6">
                  Без оборотов и деятельности. Московская регистрация для престижного старта
                  бизнеса.
                </p>
                <ul className="text-sm space-y-2 text-left">
                  <li>• Возраст от 1 года</li>
                  <li>• Московский адрес</li>
                  <li>• Готовые документы</li>
                  <li>• От 50 000 ₽</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📈</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">ООО с оборотом</h3>
                <p className="text-muted-foreground mb-6">
                  С историей операций в Москве. Подходят для федеральных тендеров и получения
                  кредитов.
                </p>
                <ul className="text-sm space-y-2 text-left">
                  <li>• История операций в Москве</li>
                  <li>• Московские банковские счета</li>
                  <li>• Кредитная история</li>
                  <li>• От 200 000 ₽</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📋</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">ООО с лицензиями</h3>
                <p className="text-muted-foreground mb-6">
                  С московскими лицензиями и СРО допусками. Сразу ведите лицензируемую деятельность
                  в столице.
                </p>
                <ul className="text-sm space-y-2 text-left">
                  <li>• Московские СРО</li>
                  <li>• Федеральные лицензии</li>
                  <li>• Образовательные</li>
                  <li>• От 400 000 ₽</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Популярные ОКВЭД в Москве */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Популярные виды деятельности в Москве
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">🏗️ Строительство и девелопмент</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Строительство зданий и сооружений</li>
                  <li>• Проектирование и архитектура</li>
                  <li>• Строительный консалтинг</li>
                  <li>• СРО в строительстве</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">💼 Консалтинг и услуги</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Управленческий консалтинг</li>
                  <li>• Аудиторская деятельность</li>
                  <li>• Юридические услуги</li>
                  <li>• Рекламная деятельность</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">💻 IT и инновации</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Разработка ПО</li>
                  <li>• IT-консалтинг</li>
                  <li>• Телекоммуникации</li>
                  <li>• Цифровые услуги</li>
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold">🏪 Торговля и логистика</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Оптовая торговля</li>
                  <li>• Импорт и экспорт</li>
                  <li>• Складская логистика</li>
                  <li>• Транспортные услуги</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Процесс покупки в Москве */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-12 text-2xl md:text-3xl font-bold text-center">
              Как купить готовое ООО в Москве
            </h2>

            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Заявка и встреча в Москве</h3>
                  <p className="text-muted-foreground">
                    Оставьте заявку с требованиями к ООО. Встретимся в московском офисе для
                    консультации и обсуждения потребностей.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Подбор московских ООО</h3>
                  <p className="text-muted-foreground">
                    В течение 2-3 дней подберем несколько московских ООО. Предоставим полную
                    информацию с учетом специфики столичного рынка.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Проверка в московских реестрах</h3>
                  <p className="text-muted-foreground">
                    Анализ в московских реестрах, ИФНС, банках. Предоставим все документы для
                    изучения.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Оформление в Москве</h3>
                  <p className="text-muted-foreground">
                    Оформляем покупку за 1-2 дня в московском офисе с нотариальным заверением.
                    Безопасная передача всех документов.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Контакты */}
        <section className="container py-12 bg-muted/20">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-8 text-2xl md:text-3xl font-bold text-center">Наш офис в Москве</h2>

            <div className="bg-white rounded-lg p-8 text-center">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold mb-4">Адрес офиса</h3>
                  <p className="text-muted-foreground mb-2">г. Москва, ул. Тверская, д. 1</p>
                  <p className="text-sm text-muted-foreground">м. Охотный ряд, Театральная</p>
                </div>

                <div>
                  <h3 className="font-semibold mb-4">Контакты</h3>
                  <p className="text-muted-foreground mb-2">
                    <a href="tel:+79254040999" className="hover:text-orange-500">
                      +7 (925) 404-09-99
                    </a>
                  </p>
                  <p className="text-sm text-muted-foreground">Ежедневно с 9:00 до 19:00</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
